/**
 * The waitlist contract — the single source of truth for a signup's shape.
 *
 * Both the browser (src/client) and the server (src/server) import this, so the
 * form and the endpoint cannot drift apart: change the shape and whichever side
 * disagrees stops compiling.
 *
 * Keep it free of runtime dependencies and of anything Node-specific. It is
 * bundled into the browser, so `fs` and `process` must never appear here.
 *
 * The server always re-validates. Client-side validation is a convenience for
 * the visitor, never a security boundary.
 */

/** Locales the marketing sites are published in. */
export const LOCALES = ['en', 'es'] as const;
export type Locale = (typeof LOCALES)[number];

export const DEFAULT_LOCALE: Locale = 'en';

export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (LOCALES as readonly string[]).includes(value);
}

/** What the browser POSTs to /api/waitlist. */
export interface WaitlistRequest {
  /** The subscriber's email address. */
  email: string;
  /** Which brand's waitlist this is, e.g. "rickey". Resolved from the hostname if omitted. */
  product?: string;
  /** Language the visitor was reading the site in — used for the confirmation email. */
  locale?: Locale;
  /**
   * Anti-spam honeypot. A real browser leaves this empty because the field is
   * hidden from humans; most naive bots fill in every input they find.
   */
  company?: string;
  /** Free-form attribution, e.g. "hero", "footer-cta". Helps see which CTA converts. */
  source?: string;
}

/** What the API sends back. `ok` discriminates the union. */
export type WaitlistResponse =
  | { ok: true; message: string; alreadySubscribed?: boolean }
  | { ok: false; error: WaitlistErrorCode; message: string };

/**
 * Stable, machine-readable failure codes. The browser maps these to translated
 * copy, so the human-readable `message` is only a fallback for non-browser callers.
 */
export type WaitlistErrorCode =
  | 'invalid_email'
  | 'missing_email'
  | 'email_too_long'
  | 'unknown_product'
  | 'rate_limited'
  | 'spam_detected'
  | 'send_failed'
  | 'server_error';

/**
 * Practical maximum length of an email address.
 * RFC 5321 caps the whole path at 254 characters.
 */
export const EMAIL_MAX_LENGTH = 254;

/**
 * Deliberately permissive email pattern.
 *
 * Fully validating an address per RFC 5322 is famously impossible with a regex,
 * and being strict here rejects real people (long TLDs, plus-addressing,
 * apostrophes). The only proof an address works is a delivered email, so this
 * check is only meant to catch obvious typos before we spend a send on them.
 */
const EMAIL_PATTERN = /^[^\s@,;:<>()[\]\\]+@[^\s@.,;:<>()[\]\\]+(\.[^\s@.,;:<>()[\]\\]+)+$/;

export interface EmailValidationResult {
  valid: boolean;
  /** Trimmed and lower-cased — safe to store and compare. */
  normalized: string;
  error?: WaitlistErrorCode;
}

/** Trim, lower-case and sanity-check an email address. */
export function validateEmail(input: unknown): EmailValidationResult {
  if (typeof input !== 'string') {
    return { valid: false, normalized: '', error: 'missing_email' };
  }

  const normalized = input.trim().toLowerCase();

  if (normalized.length === 0) {
    return { valid: false, normalized, error: 'missing_email' };
  }
  if (normalized.length > EMAIL_MAX_LENGTH) {
    return { valid: false, normalized, error: 'email_too_long' };
  }
  if (!EMAIL_PATTERN.test(normalized)) {
    return { valid: false, normalized, error: 'invalid_email' };
  }

  return { valid: true, normalized };
}
